import Accessor from "@arcgis/core/core/Accessor";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import Collection from "@arcgis/core/core/Collection";
import { when } from "@arcgis/core/core/reactiveUtils";
import PortalItem from "@arcgis/core/portal/PortalItem";
import request from "@arcgis/core/request";
import { AppData, ResourceItem } from "../../../app/widgets/AppTile";
import { isUserAccountPublic } from "../../../app/utils/appUtils";
import { DEFAULT_SORT_MYAPPS } from "../../ui/NavBar/NavBarViewModel";
import { getBaseUrl } from "../../utils/utils";
import AppTypeTile from "../../../app/widgets/AppTypeTile";

@subclass("Landing.data.MyApps.MyAppsViewModel")
export default class MyAppsViewModel extends Accessor {
  @property() view: __esri.MapView | __esri.SceneView;

  @property() portal: __esri.Portal;

  @property() myApps: __esri.Collection<__esri.PortalItem>;

  @property() isMyAppsLoading: boolean = false;

  @property({ readOnly: true }) totalFromQuery: number;

  @property({ readOnly: true }) start: number = 1;

  @property({ readOnly: true }) num: number = 20;

  @property({ readOnly: true }) sortField: "modified" | "title" =
    DEFAULT_SORT_MYAPPS as any;

  @property({ readOnly: true }) filterText: string;

  @property() contentLevel: "myApps" | "orgApps" = "myApps";

  @property() sharingLevel: "owner" | "org" | "public";

  @property() templatesFilter: string[];

  /** Tells if the signed-in user is Public */
  @property() isPublicAcct: boolean;

  @property()
  appDataMemoizationTable: Map<string, any> = new Map(); // <portalId, AppData JSON>

  @property()
  resourceDataMemoizationTable: Map<string, ResourceItem> = new Map(); // <portalId, resourceItem>

  /**
   * This is specifically for Template Items that aren't currently in the Create tab
   * (which would be deprecated ones or one made in a different locale than the current browser session's)
   */
  @property()
  templateItemMemoizationTable: Map<string, AppTypeTile> = new Map(); // <portalId, template's portalItem>

  private _baseUrl: string;
  /**
   * When a client side filter (currently only Templates)
   * is used this flag will change the operation of the MyApps Query
   */
  private _usingClientSideFilter: boolean = false;

  private _previousQueryStr: string;
  private _previousQueryRes: __esri.Collection<__esri.PortalItem>;

  constructor(params?: any) {
    super(params);

    when(
      () => this?.portal !== undefined,
      async () => {
        await this.portal.load();
        this._baseUrl = getBaseUrl(this.portal);
        this._baseUrl = this._baseUrl.replace("http://", "https://");

        this.isPublicAcct = isUserAccountPublic(this.portal);

        this.performQuery();
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  setStartAndRequery(newStart: number) {
    this._set("start", newStart);
    this.performQuery();
  }

  setSortAndRequery(sort: string) {
    if (sort !== this.sortField) {
      this._set("sortField", sort);
      this._resetToFirstPage();
      this.performQuery();
    }
  }

  private _debounceHelper: number = 0;
  setFilterAndRequery(filter: string) {
    this._debounceHelper = this._debounceHelper + 1;
    const localNum = this._debounceHelper;
    setTimeout(() => {
      if (localNum === this._debounceHelper) {
        this._set("filterText", filter);
        this._resetToFirstPage();
        this.performQuery();

        // reset debounce helper
        this._debounceHelper = 0;
      }
    }, 500);
  }

  setSharingLevelAndRequery(level: any) {
    if (level !== this.sharingLevel) {
      this.sharingLevel = level;
      this._resetToFirstPage();
      this.performQuery();
    }
  }

  setContentLevelAndRequery(contentLevel: any) {
    if (contentLevel !== this.contentLevel) {
      this.contentLevel = contentLevel;
      this._resetToFirstPage();
      this.performQuery();
    }
  }

  setTemplatesFilterAndRequery(templateUrls: string[]) {
    this.templatesFilter = templateUrls;
    this._usingClientSideFilter = templateUrls?.length !== 0;
    this._resetToFirstPage();
    this.performQuery();
  }

  /**
   * Uses memoization cache to speed up AppData
   * lookups during browser session
   */
  async pullAppDataMemoized(item: __esri.PortalItem): Promise<AppData> {
    const data = this.appDataMemoizationTable.get(item.id);
    if (data) {
      return data;
    } else {
      const pulledData = await item.fetchData("json");
      this.appDataMemoizationTable.set(item.id, pulledData);
      return pulledData;
    }
  }

  async performQuery(bustCache?: boolean) {
    this.isMyAppsLoading = true;
    const appInfos = await this.queryMyApps(bustCache);
    const loadedItems = appInfos
      .map(async (info) => {
        // note: info has all the information for a PortalItem, so spreading it into the constructor works.
        // The only problem is that these PortalItem's "loaded" props will indicate that it's not loaded since
        // the load() method wasn't called. But all the info from a call to load() will be present in these items
        if (info.isOrgItem != null) {
          delete info.isOrgItem;
        }
        return new PortalItem({ ...info, portal: this.portal } as any);
      })
      .toArray();
    this.myApps = new Collection(await Promise.all(loadedItems));
    this.isMyAppsLoading = false;
  }

  async removeFromMyAppsOnDelete(id: string) {
    this.myApps = this.myApps.filter(
      (myApp: __esri.PortalItem) => myApp.id !== id
    );
    if (this._previousQueryRes) {
      this._previousQueryRes = this._previousQueryRes.filter(
        (myApp: __esri.PortalItem) => myApp.id !== id
      );
    }
  }

  private async queryMyApps(
    bustCache?: boolean
  ): Promise<__esri.Collection<any>> {
    let qParams: any = {
      num: this.num,
      start: this.start,
      q: this.generateQuery(),
      sortField: this.sortField,
      sortOrder: this.sortField === "modified" ? "desc" : "asc",
      f: "json",
    };

    if (this._usingClientSideFilter) {
      return await this.queryWithClientSideFilter(qParams, bustCache);
    } else {
      return await this.queryOnlyServerSide(qParams);
    }
  }

  private generateQuery(): string {
    let queryParts = [];

    const { username, orgId } = this.portal.user;

    if (this.contentLevel === "myApps") {
      queryParts.push(`owner:\"${username}\"`);
      // queryParts.push("ownerfolder:root");
    }

    if (!this.isPublicAcct) {
      queryParts.push(`orgid:\"${orgId}\"`);
    }

    if (this.filterText != null && this.filterText !== "") {
      queryParts.push(`title:"*${this.filterText}*"`);
    }

    if (this.sharingLevel === "owner") {
      queryParts.push("(access:private OR access:shared)");
    } else if (this.sharingLevel === "org") {
      queryParts.push("(access:org)");
    } else if (this.sharingLevel === "public") {
      queryParts.push("(access:public)");
    }

    queryParts.push('(typekeywords:"configurableApp")');
    queryParts.push('-type:("Code Attachment")');
    queryParts.push('-typekeywords:("MapAreaPackage")');
    queryParts.push('-type:("Map Area" OR "Indoors Map Configuration")');

    return queryParts.join(" AND ");
  }

  private async queryWithClientSideFilter(
    qParams: any,
    bustCache?: boolean
  ): Promise<__esri.Collection<any>> {
    let results: __esri.Collection<__esri.PortalItem> = new Collection();

    qParams.num = 100;

    if (
      this._calcSerializedQuery(qParams) !== this._previousQueryStr ||
      bustCache
    ) {
      // The new server side query hasn't been done before

      while (qParams.start !== -1) {
        const queryResult = await request(
          `${this._baseUrl}/sharing/rest/search`,
          {
            query: qParams,
            method: "auto",
            responseType: "json",
          }
        );
        const { data } = queryResult;
        results.addMany(data.results);
        qParams.start = data.nextStart;
      }

      this._previousQueryStr = this._calcSerializedQuery(qParams);
      this._previousQueryRes = results.clone();
    } else {
      results = this._previousQueryRes;
    }

    // Apply Templates client side filter
    results = results.filter((item) => {
      return this.templatesFilter
        .map((template) => {
          return item.url.indexOf(template) !== -1;
        })
        .reduce((curr, acc) => curr || acc, false);
    });

    this._set("totalFromQuery", results.length);

    // Only send back 20 to fit the pagination
    return results.slice(this.start - 1, this.start - 1 + this.num);
  }

  private async queryOnlyServerSide(
    qParams: any
  ): Promise<__esri.Collection<any>> {
    let results: __esri.Collection<__esri.PortalItem> = new Collection();

    const queryResult = await request(`${this._baseUrl}/sharing/rest/search`, {
      query: qParams,
      method: "auto",
      responseType: "json",
    });
    const { data } = queryResult;
    this._set("totalFromQuery", data.total);
    results.addMany(data.results);
    qParams.start = data.nextStart;

    return results;
  }

  private _resetToFirstPage() {
    this._set("start", 1);
  }

  private _calcSerializedQuery(q: any): string {
    return `${q.q}-${q.sortField}`;
  }
}
